import { useState, useEffect } from 'react';
import { useObjectsTab, ObjectModel } from './hooks/HObjects';
import useWatch from './hooks/HWatch';
import { LoadingSpinner } from '../utils/LoadingSpinner';
import Button from '@mui/material/Button';
import { Snackbar, Alert, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Pagination, Typography, Chip, Select, MenuItem } from '@mui/material';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import GObjectsTable from '../tables/GObjectsTable';
import { User } from './interfaces/IUsers';
import ObjectQuickForm from '../dialogs/ObjectQuickForm';
import ObjectAccessRequestDialog from '../dialogs/ObjectAccessDialog';
import { getAccessRequestMessage } from '../utils/helpers';

const OBJECT_ASSET_CLASS_OPTIONS = ['RE', 'Funds', 'Collections', 'MA'] as const;
const PAGE_SIZE = 10;

interface ObjectsTabProps {
    onTabClick?: (tab: string, object?: any) => void;
    user?: User | null;
    filter?: string;
    watchlist?: boolean;
}

/**
 * ObjectsTab displays a table of objects with search, filter, sort, and CRUD operations.
 * Translates ObjectController.dart and objects_tab.dart to React.
 *
 * @param {ObjectsTabProps} props - Props for ObjectsTab.
 * @returns {JSX.Element} The rendered ObjectsTab component.
 */
export default function ObjectsTab({ onTabClick, user, filter = 'RE', watchlist = false }: ObjectsTabProps) {
    const {
        allObjects,
        loading,
        error,
        selectedRows,
        loadUserObjects,
        getObjectsByUserId,
        createQuickObject,
        updateObject,
        deleteObject,
        toggleRowSelection,
        selectAllRows,
        fetchAllTypes,
        fetchAllStatus,
        fetchAllZonings,
    } = useObjectsTab();

    const [searchInput, setSearchInput] = useState('');
    const [filterInput, setFilterInput] = useState(filter);
    const [displayedObjects, setDisplayedObjects] = useState<ObjectModel[]>([]);
    const [page, setPage] = useState(1);
    const [accessibleObjectIds, setAccessibleObjectIds] = useState<Set<string>>(new Set());
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error';
    }>({ open: false, message: '', severity: 'success' });

    // Dropdown filter state
    const [typeFilter, setTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [zoningFilter, setZoningFilter] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');
    const [typeOptions, setTypeOptions] = useState<string[]>([]);
    const [statusOptions, setStatusOptions] = useState<string[]>([]);
    const [zoningOptions, setZoningOptions] = useState<string[]>([]);

    // Watchlist support
    const userId = user?.id ?? user?.user_id ?? null;
    const { watchList, loadWatchList } = useWatch(userId);

    // Load watchlist when this tab is in watchlist mode and the userId changes
    useEffect(() => {
        if (watchlist) {
            void loadWatchList();
        }
    }, [watchlist, userId, loadWatchList]);

    // Form states for add dialog
    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [formData, setFormData] = useState<Partial<ObjectModel>>({
        name: '',
        assetclass: OBJECT_ASSET_CLASS_OPTIONS[0],
        company: '',
        description: '',
    });

    // Delete confirmation dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<ObjectModel | null>(null);
    const [accessRequestDialogOpen, setAccessRequestDialogOpen] = useState(false);
    const [accessRequestObjectId, setAccessRequestObjectId] = useState<string | number | null>(null);

    // Load objects on mount
    useEffect(() => {
        // console.log('[ObjectsTab] triggering loadAllObjects', {
        //     propUser: user,
        //     filterInput,
        // });

        void loadUserObjects(user, filterInput);
    }, [loadUserObjects, filterInput, user]);

    useEffect(() => {
        const loadAccessibleObjects = async () => {
            const objects = await getObjectsByUserId(userId, filterInput);
            setAccessibleObjectIds(new Set(objects.map((item) => String(item.id))));
        };

        void loadAccessibleObjects();
    }, [getObjectsByUserId, filterInput, userId]);

    useEffect(() => {
        // console.log('[ObjectsTab] allObjects updated', {
        //     count: allObjects.length,
        //     loading,
        //     error,
        // });
    }, [allObjects, loading, error]);

    useEffect(() => {
        const query = searchInput.trim().toLowerCase();
        let next = query
            ? allObjects.filter((item) => (item.name ?? '').toLowerCase().includes(query))
            : allObjects;
        if (typeFilter) next = next.filter((item) => String(item.type_ ?? '').toLowerCase() === typeFilter.toLowerCase());
        if (statusFilter) next = next.filter((item) => String(item.status ?? '').toLowerCase() === statusFilter.toLowerCase());
        if (zoningFilter) next = next.filter((item) => String(item.zoning ?? '').toLowerCase() === zoningFilter.toLowerCase());
        if (companyFilter) next = next.filter((item) => String(item.company_name ?? '').trim() === companyFilter);
        // If this tab is requested as a watchlist, filter objects to those present in the user's watch list
        if (watchlist) {
            // fallback: derive watched ids from watchList entries
            const watchedIds = new Set<string>((watchList || []).map((w: any) => String(w.object_id ?? w.id ?? w.objectId ?? w.object_id)));
            next = next.filter((item) => watchedIds.has(String(item.id)));
        }
        setDisplayedObjects(next);
        setPage(1);
    }, [allObjects, searchInput, typeFilter, statusFilter, zoningFilter, companyFilter, watchList, watchlist]);

    useEffect(() => {
        setFilterInput(filter || 'RE');
    }, [filter]);

    // Load dropdown options on mount
    useEffect(() => {
        fetchAllTypes().then((rows) => setTypeOptions(rows.map((r) => r.description ?? '').filter(Boolean)));
        fetchAllStatus().then((rows) => setStatusOptions(rows.map((r) => r.description ?? '').filter(Boolean)));
        fetchAllZonings().then((rows) => setZoningOptions(rows.map((r) => r.description ?? '').filter(Boolean)));
    }, [fetchAllTypes, fetchAllStatus, fetchAllZonings]);

    /**
     * Handle search input change
     */
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const query = event.target.value;
        setSearchInput(query);
    };

    /**
     * Handle add object button click
     */
    const handleAddObject = () => {
        setFormData({ name: '', assetclass: OBJECT_ASSET_CLASS_OPTIONS[0], company: '', description: '' });
        setFormDialogOpen(true);
    };

    /**
     * Handle edit object from table form
     */
    const handleEditObject = async (object: ObjectModel, updatedData: Partial<ObjectModel>) => {
        try {
            const success = await updateObject(object.id, updatedData);
            if (success) {
                setSnackbar({
                    open: true,
                    message: 'Object updated successfully.',
                    severity: 'success',
                });
                return;
            }

            setSnackbar({
                open: true,
                message: 'Failed to update object.',
                severity: 'error',
            });
        } catch (err: any) {
            setSnackbar({
                open: true,
                message: err.message || 'Failed to save object.',
                severity: 'error',
            });
        }
    };

    /**
     * Handle form submission (add)
     */
    const handleFormSubmit = async () => {
        if (!formData.name || !formData.name.trim()) {
            setSnackbar({
                open: true,
                message: 'Object name is required.',
                severity: 'error',
            });
            return;
        }

        try {
            const success = await createQuickObject(formData);
            if (success) {
                setSnackbar({
                    open: true,
                    message: 'Object created successfully.',
                    severity: 'success',
                });
            }

            if (success) {
                setFormDialogOpen(false);
                setFormData({ name: '', assetclass: OBJECT_ASSET_CLASS_OPTIONS[0], company: '', description: '' });
            }
        } catch (err: any) {
            setSnackbar({
                open: true,
                message: err.message || 'Failed to save object.',
                severity: 'error',
            });
        }
    };

    /**
     * Handle delete object confirmation
     */
    const handleDeleteClick = (object: ObjectModel) => {
        setDeleteTarget(object);
        setDeleteDialogOpen(true);
    };

    /**
     * Handle delete confirmation
     */
    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;

        try {
            const success = await deleteObject(deleteTarget);
            if (success) {
                setSnackbar({
                    open: true,
                    message: 'Object deleted successfully.',
                    severity: 'success',
                });
            } else {
                setSnackbar({
                    open: true,
                    message: 'Failed to delete object.',
                    severity: 'error',
                });
            }
        } catch (err: any) {
            setSnackbar({
                open: true,
                message: err.message || 'Failed to delete object.',
                severity: 'error',
            });
        } finally {
            setDeleteDialogOpen(false);
            setDeleteTarget(null);
        }
    };

    const handleOpenObjectDetails = (object: ObjectModel) => {
        const objectId = object?.id;
        if (objectId === null || objectId === undefined) return;

        if (!accessibleObjectIds.has(String(objectId))) {
            setAccessRequestObjectId(objectId);
            setAccessRequestDialogOpen(true);
            return;
        }

        const hash = `#objectdetails/${encodeURIComponent(String(objectId))}`;
        window.location.hash = hash;
        // pass current watchList (if any) so ObjectDetails can use it for UI
        onTabClick?.('objectdetails', { ...object, user, watchList });
    };

    const handleAccessRequestSubmit = () => {
        const message = getAccessRequestMessage(user, 'Access request submitted');
        setSnackbar({
            open: true,
            message,
            severity: 'success',
        });
        setAccessRequestDialogOpen(false);
        setAccessRequestObjectId(null);
    };

    return (
        <div className='standard-div'>
            <div className='standard-div-flex'>
                <div className='deals-title-box'>
                    <h1 style={{ margin: 0 }}>Deals </h1>
                    {!loading && (
                        <Chip
                            label={`${displayedObjects.length} object${displayedObjects.length !== 1 ? 's' : ''}`}
                            size='small'
                            color='primary'
                            variant='outlined'
                        />
                    )}
                </div>

                <div className='header-flex'>
                    <Select
                        size='small'
                        displayEmpty
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className='gold-select'
                    >
                        <MenuItem value=''>All Types</MenuItem>
                        {typeOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </Select>
                    <Select
                        size='small'
                        displayEmpty
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className='gold-select'
                    >
                        <MenuItem value=''>All Statuses</MenuItem>
                        {statusOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </Select>
                    <Select
                        size='small'
                        displayEmpty
                        value={zoningFilter}
                        onChange={(e) => setZoningFilter(e.target.value)}
                        className='gold-select'
                    >
                        <MenuItem value=''>All Zonings</MenuItem>
                        {zoningOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </Select>
                    <Select
                        size='small'
                        displayEmpty
                        value={companyFilter}
                        onChange={(e) => setCompanyFilter(e.target.value)}
                        className='gold-select'
                    >
                        <MenuItem value=''>All Companies</MenuItem>
                        {Array.from(new Set(allObjects.map((o: any) => String(o.company_name ?? '').trim()).filter(Boolean))).sort().map((c) => (
                            <MenuItem key={c} value={c}>{c}</MenuItem>
                        ))}
                    </Select>
                    <TextField
                        size='small'
                        placeholder='Search objects by name...'
                        value={searchInput}
                        onChange={handleSearchChange}
                        className='search-field'
                    />
                    <Button
                        variant='contained'
                        onClick={handleAddObject}
                        className='gold-button'
                    >
                        New Deal
                    </Button>
                </div>

                <ObjectQuickForm
                    open={formDialogOpen}
                    onClose={() => setFormDialogOpen(false)}
                    formData={formData}
                    setFormData={setFormData}
                    assetClassOptions={OBJECT_ASSET_CLASS_OPTIONS}
                    onSubmit={handleFormSubmit}
                />

                <ObjectAccessRequestDialog
                    open={accessRequestDialogOpen}
                    objectId={accessRequestObjectId}
                    user={user}
                    onClose={() => {
                        setAccessRequestDialogOpen(false);
                        setAccessRequestObjectId(null);
                    }}
                    onSubmit={handleAccessRequestSubmit}
                />

                {/* Delete Confirmation Dialog */}
                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                    <DialogTitle>Confirm Deletion</DialogTitle>
                    <DialogContent>
                        Are you sure you want to delete object "{deleteTarget?.name}"?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleDeleteConfirm} variant='contained' color='error'>Delete</Button>
                    </DialogActions>
                </Dialog>

                {loading ? (
                    <LoadingSpinner text='Loading objects...' />
                ) : displayedObjects.length === 0 ? (
                    <div className='deals-empty-state'>
                        <SearchOffIcon className='deals-empty-icon' />
                        <Typography variant='h6' color='text.secondary'>
                            No objects found
                        </Typography>
                        <Typography variant='body2' color='text.disabled'>
                            {searchInput ? `No results for "${searchInput}"` : 'There are no objects matching the current filter.'}
                        </Typography>
                    </div>
                ) : (
                    <>
                        <GObjectsTable
                            objects={displayedObjects.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)}
                            selectedRows={selectedRows}
                            onToggleSelect={toggleRowSelection}
                            onToggleSelectAll={(select) => selectAllRows(select)}
                            onEdit={handleEditObject}
                            onDelete={handleDeleteClick}
                            user={user}
                            onRowClick={handleOpenObjectDetails}
                        />
                        <div className='deals-pagination-row'>
                            <Typography variant='body2' color='text.secondary'>
                                {displayedObjects.length} object{displayedObjects.length !== 1 ? 's' : ''}
                                {searchInput ? ` matching "${searchInput}"` : ''}
                            </Typography>
                            {displayedObjects.length > PAGE_SIZE && (
                                <Pagination
                                    count={Math.ceil(displayedObjects.length / PAGE_SIZE)}
                                    page={page}
                                    onChange={(_, value) => setPage(value)}
                                    size='small'
                                    color='primary'
                                    shape='rounded'
                                />
                            )}
                        </div>
                    </>
                )}

                {/* Snackbar for notifications */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </div>
        </div>
    );
}
